import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';

interface SignaturePadProps {
  onSignatureChange: (base64: string | null) => void;
  width?: number;
  height?: number;
  penColor?: string;
  backgroundColor?: string;
  language?: 'en' | 'es';
}

const translations = {
  en: {
    signHere: 'Sign here',
    clear: 'Clear',
    signatureRequired: 'Signature required'
  },
  es: {
    signHere: 'Firme aquí',
    clear: 'Borrar',
    signatureRequired: 'Firma requerida'
  }
};

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onSignatureChange,
  width,
  height = 200,
  penColor = '#000000',
  backgroundColor = '#ffffff',
  language = 'en'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const hasSignatureRef = useRef(false); // Ref for immediate access in callbacks
  const [canvasWidth, setCanvasWidth] = useState(width || 300);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const t = translations[language];

  // Handle responsive width
  useEffect(() => {
    if (!width && containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width - 4; // Account for border
          if (newWidth > 0) {
            setCanvasWidth(newWidth);
          }
        }
      });

      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [width]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with device pixel ratio for sharp lines
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Fill with background color
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, height);

    // Set up drawing style
    ctx.strokeStyle = penColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [canvasWidth, height, penColor, backgroundColor]);

  // Get coordinates from touch/mouse event
  const getCoordinates = useCallback((e: React.TouchEvent | React.MouseEvent): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      // Touch event
      if (e.touches.length === 0) return null;
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else {
      // Mouse event
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  }, []);

  // Start drawing
  const startDrawing = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;

    setIsDrawing(true);
    lastPointRef.current = coords;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    // Start a new path and draw a dot at the starting point
    ctx.beginPath();
    ctx.arc(coords.x, coords.y, 1, 0, Math.PI * 2);
    ctx.fill();
  }, [getCoordinates]);

  // Draw
  const draw = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    if (!isDrawing) return;

    const coords = getCoordinates(e);
    if (!coords || !lastPointRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    lastPointRef.current = coords;
    hasSignatureRef.current = true;
    setHasSignature(true);
  }, [isDrawing, getCoordinates]);

  // Stop drawing
  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      lastPointRef.current = null;

      // Emit signature data - use ref for immediate check (state may not be updated yet)
      const canvas = canvasRef.current;
      if (canvas && hasSignatureRef.current) {
        // Get image data as PNG base64
        const dataUrl = canvas.toDataURL('image/png');
        // Remove the data:image/png;base64, prefix
        const base64 = dataUrl.split(',')[1];
        onSignatureChange(base64);
      }
    }
  }, [isDrawing, onSignatureChange]);

  // Clear signature
  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, height);

    hasSignatureRef.current = false;
    setHasSignature(false);
    onSignatureChange(null);
  }, [canvasWidth, height, backgroundColor, onSignatureChange]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <div
        style={{
          position: 'relative',
          border: `2px solid ${hasSignature ? '#22c55e' : '#d1d5db'}`,
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: backgroundColor,
          touchAction: 'none' // Prevent scrolling while drawing
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
          style={{
            display: 'block',
            cursor: 'crosshair'
          }}
        />

        {/* Placeholder text when empty */}
        {!hasSignature && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#9ca3af',
              fontSize: '16px',
              pointerEvents: 'none',
              userSelect: 'none'
            }}
          >
            {t.signHere}
          </div>
        )}

        {/* Signature line */}
        <div
          style={{
            position: 'absolute',
            bottom: '30px',
            left: '20px',
            right: '20px',
            height: '1px',
            backgroundColor: '#d1d5db',
            pointerEvents: 'none'
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '20px',
            fontSize: '12px',
            color: '#9ca3af',
            pointerEvents: 'none'
          }}
        >
          X
        </div>
      </div>

      {/* Clear button */}
      <button
        type="button"
        onClick={clearSignature}
        disabled={!hasSignature}
        style={{
          marginTop: '12px',
          padding: '10px 20px',
          backgroundColor: hasSignature ? '#ef4444' : '#e5e7eb',
          color: hasSignature ? 'white' : '#9ca3af',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: hasSignature ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          width: '100%'
        }}
      >
        <Trash2 size={18} />
        {t.clear}
      </button>
    </div>
  );
};

export default SignaturePad;
